import { useState, useEffect, useCallback, useRef } from 'react';
import type { FeatureModule, FeatureMode, FeatureModeChangeRequest } from '@/types/features';

const STORAGE_KEY = 'feature-settings';
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '';

// Initialize default feature modules
// apiRequired: true means the module only has demo data and cannot be switched to live mode
// Based on analysis of data-service.ts and backend controllers:
// - Modules with full API support: assets, locations, categories, equipment, disposalReasons, depreciation, revaluation, additions, auditTrail
// - Modules with demo-only data: dashboard, checkinout, compliance, insights, assetHistory, importAssets, importTransactions, help
const getDefaultModules = (): Record<string, FeatureModule> => ({
  dashboard: {
    id: 'dashboard',
    name: 'Dashboard',
    description: 'Central command center for asset overview and key metrics',
    icon: 'LayoutDashboard',
    mode: 'demo',
    status: 'available',
    category: 'core-operations',
    apiRequired: true, // Dashboard KPIs have limited API support
    documentationUrl: '/docs/dashboard',
  },
  assets: {
    id: 'assets',
    name: 'Assets',
    description: 'Complete asset registry and lifecycle management',
    icon: 'Package',
    mode: 'demo',
    status: 'available',
    category: 'core-operations',
    apiRequired: false, // Full API support via AssetsController
    documentationUrl: '/docs/assets',
  },
  additions: {
    id: 'additions',
    name: 'Additions',
    description: 'Track new asset acquisitions and entries',
    icon: 'PlusCircle',
    mode: 'demo',
    status: 'available',
    category: 'transaction-management',
    apiRequired: false, // Full API support via AdditionBatchesController
  },
  depreciation: {
    id: 'depreciation',
    name: 'Depreciation',
    description: 'Automated depreciation calculations and schedules',
    icon: 'TrendingDown',
    mode: 'demo',
    status: 'available',
    category: 'transaction-management',
    apiRequired: false, // Full API support via DepreciationController/DepreciationBatchesController
  },
  revaluation: {
    id: 'revaluation',
    name: 'Revaluation',
    description: 'Asset value adjustments and market valuations',
    icon: 'RefreshCw',
    mode: 'demo',
    status: 'available',
    category: 'transaction-management',
    apiRequired: false, // Full API support via RevaluationBatchesController
  },
  checkinout: {
    id: 'checkinout',
    name: 'Check-In / Check-Out',
    description: 'Asset assignment and tracking system',
    icon: 'ArrowLeftRight',
    mode: 'demo',
    status: 'available',
    category: 'operational-tools',
    apiRequired: true, // No backend controller found - demo only
  },
  compliance: {
    id: 'compliance',
    name: 'Compliance',
    description: 'Regulatory compliance monitoring and alerts',
    icon: 'Shield',
    mode: 'demo',
    status: 'available',
    category: 'operational-tools',
    apiRequired: true, // ComplianceController exists but limited functionality
  },
  insights: {
    id: 'insights',
    name: 'Insights',
    description: 'Data visualization and trend analysis',
    icon: 'BarChart3',
    mode: 'demo',
    status: 'available',
    category: 'analytics-reporting',
    apiRequired: true, // No backend controller found - demo only
  },
  assetHistory: {
    id: 'assetHistory',
    name: 'Asset History',
    description: 'Complete historical record of all asset activities',
    icon: 'History',
    mode: 'demo',
    status: 'available',
    category: 'analytics-reporting',
    apiRequired: true, // Uses asset audit data - limited API support
  },
  auditTrail: {
    id: 'auditTrail',
    name: 'Audit Trail',
    description: 'Comprehensive logging of all system changes',
    icon: 'FileText',
    mode: 'demo',
    status: 'available',
    category: 'analytics-reporting',
    apiRequired: false, // Full API support via AuditController
  },
  importAssets: {
    id: 'importAssets',
    name: 'Import Assets',
    description: 'Bulk asset data upload functionality',
    icon: 'Upload',
    mode: 'demo',
    status: 'available',
    category: 'system-administration',
    apiRequired: false, // Full API support via ImportController
  },
  importTransactions: {
    id: 'importTransactions',
    name: 'Import Transactions',
    description: 'Bulk transaction processing',
    icon: 'FileUp',
    mode: 'demo',
    status: 'available',
    category: 'system-administration',
    apiRequired: true, // Limited API support for transaction imports
  },
  locations: {
    id: 'locations',
    name: 'Locations',
    description: 'Manage physical and logical locations',
    icon: 'MapPin',
    mode: 'demo',
    status: 'available',
    category: 'configuration',
    apiRequired: false, // Full API support via SettingsController
  },
  categories: {
    id: 'categories',
    name: 'Categories',
    description: 'Asset categorization structure',
    icon: 'FolderTree',
    mode: 'demo',
    status: 'available',
    category: 'configuration',
    apiRequired: false, // Full API support via SettingsController
  },
  equipment: {
    id: 'equipment',
    name: 'Equipment',
    description: 'Equipment type definitions',
    icon: 'Wrench',
    mode: 'demo',
    status: 'available',
    category: 'configuration',
    apiRequired: false, // Full API support via SettingsController
  },
  disposalReasons: {
    id: 'disposalReasons',
    name: 'Disposal Reasons',
    description: 'Predefined disposal categorization',
    icon: 'Trash2',
    mode: 'demo',
    status: 'available',
    category: 'configuration',
    apiRequired: false, // Full API support via SettingsController
  },
  help: {
    id: 'help',
    name: 'Help & Support',
    description: 'Documentation and support resources',
    icon: 'HelpCircle',
    mode: 'demo',
    status: 'available',
    category: 'configuration',
    apiRequired: true, // Static content - no API needed
  },
});

export const useFeatureSettings = () => {
  const [modules, setModules] = useState<Record<string, FeatureModule>>(() => {
    // Load from localStorage on initial mount
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        return parsed.modules || getDefaultModules();
      } catch (e) {
        console.error('Failed to parse stored feature settings:', e);
      }
    }
    return getDefaultModules();
  });

  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [recentlyChanged, setRecentlyChanged] = useState<string | null>(null);
  const [isDirty, setIsDirty] = useState(false);
  const [lastSavedModules, setLastSavedModules] = useState<Record<string, FeatureModule> | null>(null);
  
  // Track the initial state to detect changes
  const initialModulesRef = useRef<Record<string, FeatureModule> | null>(null);
  
  // Initialize the reference to track changes
  useEffect(() => {
    if (initialModulesRef.current === null) {
      initialModulesRef.current = modules;
    }
  }, [modules]);

  // Save to localStorage whenever modules change (for offline cache)
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({
      modules,
      lastUpdated: new Date().toISOString(),
      version: '1.0.0',
    }));
  }, [modules]);

  // Clear recently changed highlight after animation
  useEffect(() => {
    if (recentlyChanged) {
      const timer = setTimeout(() => {
        setRecentlyChanged(null);
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [recentlyChanged]);

  const switchMode = useCallback(async (
    moduleId: string,
    newMode: FeatureMode,
    userId?: string
  ): Promise<boolean> => {
    setIsLoading(true);
    
    try {
      // Simulate API call delay
      await new Promise(resolve => setTimeout(resolve, 500));

      setModules(prev => ({
        ...prev,
        [moduleId]: {
          ...prev[moduleId],
          mode: newMode,
          lastSwitched: new Date(),
          switchedBy: userId || 'current-user',
        },
      }));

      setRecentlyChanged(moduleId);
      setIsDirty(true);
      
      // Log the change for audit purposes
      const changeRequest: FeatureModeChangeRequest = {
        moduleId,
        fromMode: modules[moduleId].mode,
        toMode: newMode,
        timestamp: new Date(),
        userId,
      };
      
      console.log('Feature mode changed:', changeRequest);
      
      return true;
    } catch (error) {
      console.error('Failed to switch mode:', error);
      return false;
    } finally {
      setIsLoading(false);
    }
  }, [modules]);

  const switchAllToMode = useCallback(async (mode: FeatureMode): Promise<boolean> => {
    setIsLoading(true);
    
    try {
      await new Promise(resolve => setTimeout(resolve, 800));

      const updatedModules = { ...modules };
      Object.keys(updatedModules).forEach(key => {
        if (updatedModules[key].status === 'available' && !updatedModules[key].apiRequired) {
          updatedModules[key] = {
            ...updatedModules[key],
            mode,
            lastSwitched: new Date(),
          };
        }
      });

      setModules(updatedModules);
      setIsDirty(true);
      return true;
    } catch (error) {
      console.error('Failed to switch all modes:', error);
      return false;
    } finally {
      setIsLoading(false);
    }
  }, [modules]);

  const getModulesByCategory = useCallback((category: string) => {
    return Object.values(modules).filter(module => module.category === category);
  }, [modules]);

  const resetToDefaults = useCallback(() => {
    setModules(getDefaultModules());
    setIsDirty(true);
    localStorage.removeItem(STORAGE_KEY);
  }, []);

  // Save settings to the backend API
  const saveSettings = useCallback(async (): Promise<boolean> => {
    setIsSaving(true);
    
    try {
      const settingsData = {
        modules,
        lastUpdated: new Date().toISOString(),
        version: '1.0.0',
      };
      
      // Try to save to API if available
      if (API_BASE_URL) {
        try {
          const response = await fetch(`${API_BASE_URL}/api/Settings/features`, {
            method: 'PUT',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(settingsData),
          });
          
          if (!response.ok) {
            console.warn('Failed to save to API, falling back to localStorage only');
          }
        } catch (apiError) {
          console.warn('API not available, using localStorage only:', apiError);
        }
      }
      
      // Always save to localStorage as cache
      localStorage.setItem(STORAGE_KEY, JSON.stringify(settingsData));
      
      setLastSavedModules(modules);
      setIsDirty(false);
      initialModulesRef.current = modules;
      
      return true;
    } catch (error) {
      console.error('Failed to save settings:', error);
      return false;
    } finally {
      setIsSaving(false);
    }
  }, [modules]);

  // Discard unsaved changes and revert to last saved state
  const discardChanges = useCallback(() => {
    if (lastSavedModules) {
      setModules(lastSavedModules);
    } else if (initialModulesRef.current) {
      setModules(initialModulesRef.current);
    } else {
      setModules(getDefaultModules());
    }
    setIsDirty(false);
  }, [lastSavedModules]);

  // Load settings from the backend API
  const loadSettings = useCallback(async (): Promise<boolean> => {
    setIsLoading(true);
    
    try {
      // Try to load from API if available
      if (API_BASE_URL) {
        try {
          const response = await fetch(`${API_BASE_URL}/api/Settings/features`);
          
          if (response.ok) {
            const data = await response.json();
            if (data.modules) {
              setModules(data.modules);
              setLastSavedModules(data.modules);
              initialModulesRef.current = data.modules;
              setIsDirty(false);
              return true;
            }
          }
        } catch (apiError) {
          console.warn('API not available, using localStorage:', apiError);
        }
      }
      
      // Fall back to localStorage
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        if (parsed.modules) {
          setModules(parsed.modules);
          setLastSavedModules(parsed.modules);
          initialModulesRef.current = parsed.modules;
          setIsDirty(false);
          return true;
        }
      }
      
      // Use defaults if nothing is stored
      const defaults = getDefaultModules();
      setModules(defaults);
      setLastSavedModules(defaults);
      initialModulesRef.current = defaults;
      setIsDirty(false);
      
      return true;
    } catch (error) {
      console.error('Failed to load settings:', error);
      return false;
    } finally {
      setIsLoading(false);
    }
  }, []);

  return {
    modules,
    isLoading,
    isSaving,
    isDirty,
    recentlyChanged,
    switchMode,
    switchAllToMode,
    getModulesByCategory,
    resetToDefaults,
    saveSettings,
    discardChanges,
    loadSettings,
  };
};
